import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from './utils/cn';

export interface DialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  closeLabel?: string;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const;

/**
 * Lightweight modal dialog. Traps focus loosely (the close button is
 * auto-focused on open) and closes on ESC or backdrop click. Not a
 * replacement for Radix Dialog if a fully a11y-rigorous experience is
 * required, but enough for the bootstrap mock surface.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeLabel = 'Close',
}: DialogProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-fg/40 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className={cn(
          'w-full rounded-2xl bg-surface border border-border shadow-lg',
          'animate-in fade-in zoom-in-95',
          SIZES[size],
        )}
      >
        <header className="flex items-start justify-between gap-3 px-6 pt-6">
          <div className="space-y-1">
            {title ? <h2 className="text-lg font-semibold tracking-tight">{title}</h2> : null}
            {description ? <p className="text-sm text-fg-muted">{description}</p> : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label={closeLabel}
            onClick={() => onOpenChange(false)}
            className="text-fg-subtle hover:text-fg rounded-md p-1 -m-1"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        {children ? <div className="px-6 py-4">{children}</div> : null}
        {footer ? (
          <footer className="px-6 pb-6 pt-2 flex flex-wrap items-center justify-end gap-2">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
